// Simple's Brand (spec ui-perf/simple-brand): its tabs, and the brands area's own pages
// (`features/brands`) inside Simple's frame, so the brand's kit, its edit, a new product and a
// product's page open, save and delete in Simple, as Pro's do in `pro-brands.tsx`. Composed here:
// a feature never imports another; the composition root may.
import { createRoute, Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { z } from "zod";

import { EditBrand } from "../features/brands/BrandFormScreens";
import { BrandPage } from "../features/brands/BrandScreen";
import { NewProduct, ProductPage } from "../features/brands/ProductScreens";
import { VoicesTab } from "../features/studio2/library/VoicesTab";
import { SimpleActors } from "../features/studio2/simple/SimpleActors";
import { BRAND_TABS, SimpleBrand } from "../features/studio2/simple/SimpleBrand";
import { SimpleIntegrations } from "../features/studio2/simple/SimpleIntegrations";
import { SimpleProducts } from "../features/studio2/simple/SimpleProducts";
import { isUuid } from "../platform/routes";
import { simpleRoute } from "./pro-simple";

const S = "/signed-in/teams/$teamId/brands/$brandId/studio/simple";

/** `?tab=`: one of the prototype's tabs; anything else is Brand kit. */
const brandSearch = z.object({ tab: z.enum(BRAND_TABS).optional().catch(undefined) });

/** `simple/brand`: the brand's kit, its products, actors, voices and integrations. */
export const simpleBrandRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "brand",
  validateSearch: (search) => brandSearch.parse(search),
  component: SimpleBrandPage,
});

/** `simple/brand/edit`: the brand Simple is on, its edit. */
export const simpleEditBrandRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "brand/edit",
  component: SimpleEditBrand,
});

/** `simple/products/new`: a new product, from a link or by hand; a saved one opens its page. */
export const simpleNewProductRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "products/new",
  component: SimpleNewProduct,
});

/** `simple/products/$productId`: the product's page; a delete goes back to Brand's Products. */
export const simpleProductRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "products/$productId",
  params: { parse: ({ productId }) => (isUuid(productId) ? { productId } : false) },
  component: SimpleProduct,
});

/** The forms' Pro look, which Simple's frame shares (`[data-pro-form]`, as `pro-brands.tsx`'s). */
function SimpleForm({ children }: { children: ReactNode }) {
  return (
    <div data-pro-form style={{ height: "100%" }}>
      {children}
    </div>
  );
}

/** A brand page's back arrow or "Edit brand": a link to one of Simple's pages. */
function toHome(teamId: string, brandId: string) {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/simple" params={{ teamId, brandId }} />
  );
}

function toBrand(teamId: string, brandId: string, tab?: "products") {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/simple/brand" params={{ teamId, brandId }} search={{ tab }} />
  );
}

function toEdit(teamId: string, brandId: string) {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/simple/brand/edit" params={{ teamId, brandId }} />
  );
}

/** The brand Simple was on is gone: the team's way in opens its first brand left, in Simple. */
function useGoneHome(teamId: string) {
  const navigate = useNavigate();
  return () => navigate({ to: "/teams/$teamId/pro", params: { teamId }, replace: true });
}

function SimpleBrandPage() {
  const { teamId, brandId } = useParams({ from: `${S}/brand` });
  const { tab } = useSearch({ from: `${S}/brand` });
  const onDeleted = useGoneHome(teamId);
  return (
    <SimpleBrand
      tab={tab ?? "kit"}
      panels={{
        kit: (
          <SimpleForm>
            <BrandPage
              teamId={teamId}
              brandId={brandId}
              back={toHome(teamId, brandId)}
              edit={toEdit(teamId, brandId)}
              onDeleted={onDeleted}
            />
          </SimpleForm>
        ),
        products: <SimpleProducts teamId={teamId} brandId={brandId} />,
        // Spec ui-perf/simple-brand-tabs: the platform's actor lists, Pro's Library's voices, and
        // the integrations.
        actors: <SimpleActors teamId={teamId} brandId={brandId} />,
        voices: <VoicesTab teamId={teamId} brandId={brandId} />,
        integrations: <SimpleIntegrations teamId={teamId} />,
      }}
    />
  );
}

// Another brand starts each page anew, as Pro's do (spec pro-studio-redesign, 31).
function SimpleEditBrand() {
  const { teamId, brandId } = useParams({ from: `${S}/brand/edit` });
  const navigate = useNavigate();
  const onDeleted = useGoneHome(teamId);
  return (
    <SimpleForm>
      <EditBrand
        key={`${teamId} ${brandId}`}
        teamId={teamId}
        brandId={brandId}
        back={toBrand(teamId, brandId)}
        onSaved={() =>
          // Saved: the form's leave prompt has nothing to ask.
          navigate({ to: "/teams/$teamId/brands/$brandId/simple/brand", params: { teamId, brandId }, ignoreBlocker: true })
        }
        onDeleted={onDeleted}
      />
    </SimpleForm>
  );
}

function SimpleNewProduct() {
  const { teamId, brandId } = useParams({ from: `${S}/products/new` });
  const navigate = useNavigate();
  return (
    <SimpleForm>
      <NewProduct
        key={`${teamId} ${brandId}`}
        teamId={teamId}
        brandId={brandId}
        back={toBrand(teamId, brandId, "products")}
        onCreated={(productId) =>
          navigate({
            to: "/teams/$teamId/brands/$brandId/simple/products/$productId",
            params: { teamId, brandId, productId },
            ignoreBlocker: true,
          })
        }
      />
    </SimpleForm>
  );
}

function SimpleProduct() {
  const { teamId, brandId, productId } = useParams({ from: `${S}/products/$productId` });
  const navigate = useNavigate();
  return (
    <SimpleForm>
      <ProductPage
        key={`${teamId} ${brandId} ${productId}`}
        teamId={teamId}
        brandId={brandId}
        productId={productId}
        back={toBrand(teamId, brandId, "products")}
        onDeleted={() =>
          // Deleted: an edit left in its form has nothing left to save.
          navigate({
            to: "/teams/$teamId/brands/$brandId/simple/brand",
            params: { teamId, brandId },
            search: { tab: "products" },
            replace: true,
            ignoreBlocker: true,
          })
        }
      />
    </SimpleForm>
  );
}
